import { Checkbox, FormControlLabel, Typography } from '@mui/material'
import { OptJobType } from 'api/optJobs/optJobs.api'
import { postStat } from 'api/stats/stats.api'
import { useSystem, useSystemAutomaticOptimizationToggleMutation } from 'api/systems/systems.api'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Tooltip from 'ui/components/Tooltip/Tooltip'

type AutomaticOptimizationToggleButtonProps = {
  optJobType: OptJobType
  permission?: UserPermission
}

export default function AutomaticOptimizationToggleButton({
  optJobType,
  permission = 'run_regular_opt',
}: AutomaticOptimizationToggleButtonProps): ReactElement | null {
  const { t } = useTranslation()
  const { systemId } = useAuth()
  const { data: activeSystem } = useSystem({ id: systemId })
  const { mutate: automaticOptimizationToggle } = useSystemAutomaticOptimizationToggleMutation()

  const hasPermission = useHasPermission(permission)
  const automaticOptStatus = activeSystem?.automatic_optimization ?? false

  // Don't show for WIP type or fuel plan type
  if (optJobType === 'regular_wip' || optJobType === 'fuel_plan') {
    return null
  }

  return (
    <div className="flex justify-center items-start">
      <Tooltip
        title={t(
          'Activates automatic optimization of the production plan every full hour to ensure that the production plan always takes into account the most current forecast and measurement values'
        )}
        placement="bottom"
        arrow
        display="block"
      >
        <div className="bg-grey-2 pl-2 rounded shadow">
          <FormControlLabel
            control={
              <Checkbox
                checked={automaticOptStatus}
                onChange={() => {
                  if (activeSystem) {
                    automaticOptimizationToggle({ id: activeSystem.id })
                    postStat(`automatic-optimization-toggle`, `click`)
                  }
                }}
                disabled={!hasPermission}
                value="updateAutomatically"
                size="small"
              />
            }
            className="text-sm"
            labelPlacement="end"
            label={
              <Typography variant="body1" className="text-sm">
                {t(`Optimize automatically`)}
              </Typography>
            }
            disabled={!hasPermission}
          />
        </div>
      </Tooltip>
    </div>
  )
}
